<template>
<easy-data-table
	class="data-table"
	table-class-name="customize-table"
	:headers="tableHeader"
	:items="tableRows"
	theme-color="rgb(198, 199, 200)"
	:table-height="height"
	:table-min-height="height"
	header-text-direction="center"
	:body-item-class-name="bodyItemClassNameFunction"
	@click-row="onRowClick"
	@mouseover="onTableMouseOver"
	@mouseleave="onTableMouseLeave"
	:loading="isLoading || isFetching"
	:no-hover="true"
	hide-footer
>
	<!-- If rows is empty -->
	<template #empty-message>
		Для метрики "{{ visibleTableLines[hoveredRowIndex]?.title }}" немає даних за період {{ chartTimeStr(timeFrom) }} - {{ chartTimeStr(timeTo) }}
	</template>

	<template #loading>
		<div class="spinner-border" style="width: 3rem; height: 3rem;" role="status">
			<span class="visually-hidden">Loading...</span>
		</div>
	</template>

	<template #header="{ text, color }">
		<span class="" :style="{'color': color ?? '#FFFFFF00'}">
			{{ text }}
		</span>
    </template>

	<template #item-title="item">
		<div class="d-flex">
			<div class="px-2">
				<svg v-show="Object.hasOwn(item, 'isExpand')" xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="none" viewBox="0 0 16 16">
					<path
						:d="
							item.isExpand
							? 'M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708'
							: 'M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708'
						"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
				</svg>
			</div>

			<div class="flex-grow-1">
				{{ item.title }}
			</div>

			<div class="">
				<span class="ps-2" :style="{'color': item.lineColor ?? '#FFFFFF00',}">
					&#9210;
				</span>
			</div>
		</div>
	</template>
</easy-data-table>
</template>




<script>
import { useStationsStore } from '@/stores/StationsStore.js'
import { useStationsHistoryStore } from '@/stores/StationsHistoryStore.js'
import { chartTimeStr, addSecondsToDate } from '@/helpers.js'
import { Station } from '@/models/station.js'

const RED = '#f2495c'
const GRAY = '#5f6368'

export default {
	components: {
	},

	props: {
		stationId: {
			type: String,
			require: true,
		},

		// Table lines
		// Array of Objects
		// {
		// 		title: 'Захисний потенціал',
		// 		showInTable: true,
		//		isExpand: false,
		// 		confidenceBand: true,
		// 		lineType: 'solid',
		// 		lineColor: '#5794f2',
		// 		unit: INFLUXDB.station_metrics.service_unit,
		// 		icon: 'service',
		// 		metric: ServiceUnit.metricKeys.eAvg,
		// }
		items: {
			type: Array,
			require: false,
			default: () => Array(),
		},
		

		// Point of interst
		poi: {
			type: Date,
			require: true,
		},

		poiRangeSeconds: {
			type: Number,
			require: true,
		},

		isLoading: {
			type: Boolean,
			require: false,
			default: false,
		},

		height: {
			type: Number,
			require: false,
			default: undefined,
		},
	},

	setup() {
		const stationsStore = useStationsStore()
		const historyStore = useStationsHistoryStore()
		const hoveredRowIndex = 0

		return {
			stationsStore,
			historyStore,
			hoveredRowIndex,
			GRAY,
			chartTimeStr,
		}
	},

	data() {
		return {
			tableHeader: [
				{ text: '11:20:00', value: '11:20' },
			],

			tableRows: [
				{'11:20': '- // - // -'}
			],

			isFetching: false,
		}
	},

	watch: {
		poi() {
			// console.log(`Table poi updated ${chartTimeStr(this.poi, true)}. Range is ${chartTimeStr(this.timeFrom)} - ${chartTimeStr(this.timeTo)}`)
			this.initTable()
		},

		visibleTableLines() {
			this.initTable()
		},
	},

	created() {},
	
	async mounted() {},

	methods: {
		async initTable() {
			if (!(this.poi instanceof Date)) {
				return
			}
			// Fetch metrics
			await this.fetchMetrics()

			// Clean table
			this.tableHeader.length = 0
			this.tableRows.length = 0

			// Create table header
			this.tableHeader.push({
				text: 'Метрика',
				value: 'title',
				color: '#373737',
			})
			for (let i = 0; i < this.maxColumnsNum; i++) {
				this.tableHeader.push({
					text: '\xa0'.repeat(15),
					value: String(i),
					color: '#373737',
				})
			}

			this.buildTableBasedOnHoveredRow()
		},

		async fetchMetrics() {
			this.isFetching = true

			// Make metrics list for fetch
			const metricsForFetch = []
			for (const line of this.visibleTableLines) {
				if (line.unit !== undefined && line.metric !== undefined) {
					metricsForFetch.push([line.unit, line.metric])
				}
			}
			
			await this.historyStore.loadMetrics(
				this.metricsId,
				metricsForFetch,
				this.timeFrom,
				this.timeTo,
				false,
			)

			this.isFetching = false
		},

		setHoveredRowTimeToTableHead() {
			const line = this.tableRows[this.hoveredRowIndex]

			// Iterate over line values, insert time to table header
			if (line && line.data) {
				if (line.data.length > 0) {
					// Clear table header meric time text
					for (let cell of this.tableHeader.slice(1)) {
						cell.text = '\xa0'.repeat(15)
					}
				}

				for (const [i, metric] of line.data.entries()) {
					const colHeader = this.tableHeader.find((el) => el.value == String(i))
					if (!colHeader || !metric) {
						continue
					}

					colHeader.text = chartTimeStr(metric.time, true)
					//if (chartTimeStr(metric.time, true) == chartTimeStr(this.poi, true)) {
					if (i == this.centerColumnIndex) {
						colHeader.color = RED
					} else {
						colHeader.color = '#373737'
					}
				}
			}
		},

		buildTableBasedOnHoveredRow() {
			const maxDepth = 100
			// console.time('buildTableBasedOnHoveredRow')

			const formatValue = (line, metric) => {
				const value = metric?.value ?? metric?.median
				if (value === undefined) {
					return '-'
				} else if (Object.hasOwn(line, 'format')) {
					return line.format(value)
				} else {
					return typeof value === 'number' ? value.toFixed(2) : value
				}
			}

			// Time point in columns
			let baseTimePoints = []

			// Get hovered row from storage as time points base
			const tableCenterColumn = Math.ceil(this.maxColumnsNum / 2)
			const hoveredRow = { ...this.visibleTableLines[this.hoveredRowIndex], data: [] }
			
			// Iterate metrics in past from POI
			for (let metricDepth = 0, colIndex = tableCenterColumn, tableColNum = 0;
				tableColNum < tableCenterColumn && metricDepth < maxDepth;
				metricDepth++
			) {
				// console.log(`Depth in past ${metricDepth}`)
				let metric = this.historyStore.getPastMetricValue(
					this.metricsId,
					hoveredRow.unit,
					hoveredRow.metric,
					this.poi,
					metricDepth,
				)

				// If POI time limit (too old metrics).
				if (metric?.time?.getTime() < this.timeFrom.getTime()) {
					// Limits iteration in depth
					break
				// Cut mean metric values
				} else if (metric?.value === undefined) {
					continue
				} else {
					colIndex--
					tableColNum++
				}

				hoveredRow.data.push([colIndex, metric])
				hoveredRow[String(colIndex)] = formatValue(
					this.visibleTableLines[this.hoveredRowIndex],
					metric,
				)

				baseTimePoints.push([colIndex, metric?.time])
			}
			
			// Iterate metrics in future from POI
			for (let metricDepth = 0, colIndex = tableCenterColumn - 1, tableColNum = 0;
				tableColNum < tableCenterColumn && metricDepth < maxDepth;
				metricDepth++
			) {
				// console.log(`Depth in future ${metricDepth}`)
				let metric = this.historyStore.getFutureMetricValue(
					this.metricsId,
					hoveredRow.unit,
					hoveredRow.metric,
					this.poi,
					metricDepth,
				)

				// If POI time limit (too fresh metrics).
				if (metric?.time?.getTime() > this.timeTo.getTime()) {
					// Limits iteration in depth
					break
				// Cut mean metric values
				} else if (metric?.value === undefined) {
					continue
				} else {
					colIndex++
					tableColNum++
				}

				hoveredRow.data.push([colIndex, metric])
				hoveredRow[String(colIndex)] = formatValue(
					this.visibleTableLines[this.hoveredRowIndex],
					metric,
				)

				baseTimePoints.push([colIndex, metric?.time])
			}
			
			// Sort by increase in time (with wright undefined positioning)
			baseTimePoints.sort((a, b) => a[0] - b[0])
			baseTimePoints = baseTimePoints.map((el) => el[1])
			//
			hoveredRow.data.sort((a, b) => a[0] - b[0])
			hoveredRow.data = hoveredRow.data.map((el) => el[1])

			// If hovered row is empty - skip rebuild table
			if (baseTimePoints.every((el) => el == undefined)) {
				// console.timeEnd('buildTableBasedOnHoveredRow')
				return
			}

			this.tableRows.length = 0

			// Fetch not hovered rows based on time points from hovered row
			for (const [i, line] of this.visibleTableLines.entries()) {
				// If hovered line
				if (i == this.hoveredRowIndex) {
					this.tableRows.push(hoveredRow)
					continue
				}

				const row = { ...line, data: [] }

				// Iterate over time points
				for (const [celIndex, time] of baseTimePoints.entries()) {
					if (time == undefined) {
						row[String(celIndex)] = ''
						continue
					}
					
					let metric = this.historyStore.getPastMetricValue(
						this.metricsId,
						row.unit,
						row.metric,
						time,
					)
					// console.log(`Update metric ${row.metric}`, metric)

					// Cut too old metrics
					if (metric?.time < this.timeFrom) {
						metric = undefined
					}

					row.data.push(metric)
					row[String(celIndex)] = formatValue(
						line,
						metric,
					)
				}

				this.tableRows.push(row)
			}

			this.setHoveredRowTimeToTableHead()

			// console.timeEnd('buildTableBasedOnHoveredRow')
		},

		bodyItemClassNameFunction(column) {
			if (column === 'title') {
				return 'direction-start'
			}

			const classes = ['direction-center']
			if (column === String(this.centerColumnIndex)) {
				classes.push('center-column-bg')
			}

			return classes.join(' ')
		},

		onTableMouseOver(event) {
			const tr = event.target.closest('tr')
			if (!tr) {
				return
			}
			// Skip table header
			if (tr.closest('thead')) {
				return
			}

			// Count only body rows (not expanded rows)
			const tbody = tr.closest('tbody')
			const allRows = Array.from(tbody.querySelectorAll('tr:not(.expand-row-no-background)'))
			const rowIndex = allRows.indexOf(tr)

			if (rowIndex === -1) {
				return
			}

			const line = this.tableRows[rowIndex]
			
			// Remove hover class from previous row
			if (this.hoveredRowIndex !== null && this.hoveredRowIndex !== rowIndex) {
				const prevTr = allRows[this.hoveredRowIndex]
				if (prevTr) {
					prevTr.classList.remove('hovered-row')
				}
			}

			// Skip expand rows
			if (Object.hasOwn(line, 'isExpand')) {
				return
			}
			
			// Add hover class to current row
			tr.classList.add('hovered-row')

			if (this.hoveredRowIndex == rowIndex) {
				return
			}
			// If row is not expandable
			if (this.visibleTableLines[rowIndex].isExpand === undefined ) {
				this.hoveredRowIndex = rowIndex
			}
			// console.log('hover row index', rowIndex, line.title)
			
			
			this.buildTableBasedOnHoveredRow()
		},

		onTableMouseLeave(/*event*/) {
			//
		},

		onRowClick(row) {
			// console.log('onRowClick', row)
			if (!row || !Object.hasOwn(row, 'isExpand')) {
				return
			}

			const line = this.visibleTableLines.find((el) => el.title == row.title)
			line.isExpand = !line.isExpand
			this.initTable()
		},
	},

	computed: {
		station() {
			return this.stationsStore.station(this.stationId)
		},

		metricsId() {
			return this.station.passport.get(Station.passportKeys.metricsId)
		},

		visibleTableLines() {
			// return this.items.filter((el) => el.showInTable == true)

			const visibleItems = this.items.filter((el) => el.showInTable == true)
			const visibleLines = []
			// Feel table lines array
			for (const item of visibleItems) {
				// Join regular lines and expandable lines in rows array
				// If expandable line and it is expanded
				if (Object.hasOwn(item, 'isExpand') && item.isExpand === true) {
					visibleLines.push(item) 					// expandable line
					visibleLines.push(...item.group.metrics)	// expanded lines
				} else {
					visibleLines.push(item)
				}
			}

			return visibleLines
		},

		// Must be odd for symetric view
		maxColumnsNum() {
			return 13
		},

		centerColumnIndex() {
			return Math.ceil(this.maxColumnsNum / 2) - 1
		},

		timeFrom() {
			return addSecondsToDate(this.poi, -(this.poiRangeSeconds / 2))
		},

		timeTo() {
			return addSecondsToDate(this.poi, this.poiRangeSeconds)
		},
	},
}
</script>




<style src="@/assets/table.css" scoped></style>
<style scoped>
</style>

<style>
	.customize-table tbody td.center-column-bg {
		background-color: #eee !important;
		color: #212121 !important;
	}
	/* Hovered row styling - persistent until another row is hovered */
	.customize-table tbody tr.hovered-row td {
		background-color: #eee !important;
		color: #212121 !important;
	}
</style>